import React, { useState } from 'react';
import { User, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import LogoutButton from './LogoutButton';
import NotificationBell from '../notifications/NotificationBell';
import LanguageToggle from '../LanguageToggle';
import GarageSwitcher from './GarageSwitcher';
import EmployeeSwitcher from '../auth/EmployeeSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

interface DashboardHeaderProps {
  title?: string;
  userName?: string;
  userRole?: string;
  showNotifications?: boolean;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  userName,
  userRole,
  showNotifications = true
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Get user info from context or localStorage
  const storedUser = localStorage.getItem('user');
  const userData = user || (storedUser ? JSON.parse(storedUser) : null);
  
  const displayName = userName || userData?.name || t('welcome');
  const displayRole = userRole || userData?.role || '';

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'super_admin':
        return t('superAdminDashboard');
      case 'admin':
        return t('adminDashboard');
      case 'garage_owner':
        return t('garageOwnerDashboard');
      case 'employee':
        return t('employeeDashboard');
      case 'customer':
        return t('customerPortal');
      default:
        return role;
    }
  };

  // Get dashboard menu items based on user role
  const getDashboardMenuItems = () => {
    const baseItems = [
      { name: t('dashboard'), href: '/dashboard' },
    ];

    switch (userData?.role) {
      case 'super_admin':
        return [
          ...baseItems,
          { name: t('garages'), href: '/dashboard/garages' },
          { name: t('users'), href: '/dashboard/users' },
          { name: t('subscriptions'), href: '/dashboard/subscriptions' },
          { name: t('reports'), href: '/dashboard/reports' },
          { name: t('settings'), href: '/dashboard/settings' },
          { name: t('adminPrivileges'), href: '/dashboard/admin-privileges' }
        ];
      case 'admin':
        return [
          ...baseItems,
          { name: t('garages'), href: '/dashboard/garages' },
          { name: t('users'), href: '/dashboard/users' },
          { name: t('subscriptions'), href: '/dashboard/subscriptions' },
          { name: t('reports'), href: '/dashboard/reports' }
        ];
      case 'garage_owner':
        return [
          ...baseItems,
          { name: t('employees'), href: '/dashboard/employees' },
          { name: t('customers'), href: '/dashboard/customers' },
          { name: t('vehicles'), href: '/dashboard/vehicles' },
          { name: t('jobCards'), href: '/dashboard/job-cards' },
          { name: t('inventory'), href: '/dashboard/inventory' },
          { name: t('invoices'), href: '/dashboard/invoices' },
          { name: t('reports'), href: '/dashboard/reports' }
        ];
      case 'employee':
        return [
          ...baseItems,
          { name: t('myTasks'), href: '/dashboard/tasks' },
          { name: t('jobCards'), href: '/dashboard/job-cards' },
          { name: t('inventory'), href: '/dashboard/inventory' },
          { name: t('customers'), href: '/dashboard/customers' }
        ];
      case 'customer':
        return [
          ...baseItems,
          { name: t('myVehicles'), href: '/dashboard/vehicles' },
          { name: t('serviceHistory'), href: '/dashboard/service-history' },
          { name: t('appointments'), href: '/dashboard/appointments' },
          { name: t('invoices'), href: '/dashboard/invoices' }
        ];
      default:
        return baseItems;
    }
  };

  const menuItems = getDashboardMenuItems();

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="text-xl sm:text-2xl font-bold" style={{ color: '#008B8B' }}>
              Rayyi7
            </Link>
          </div>

          {/* Right side items */}
          <div className="flex items-center">
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-6 mr-6">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-[#008B8B] transition font-medium"
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Action buttons container */}
            <div className="flex items-center">
              {/* Employee Switcher - Only for garage staff - Hidden on small mobile */}
              {(userData?.role === 'garage_owner' || userData?.role === 'employee') && (
                <div className="hidden sm:block">
                  <EmployeeSwitcher />
                </div>
              )}

              {/* Garage Switcher - Only for garage staff - Hidden on small mobile */}
              {(userData?.role === 'garage_owner' || userData?.role === 'employee') && (
                <div className="hidden sm:block">
                  <GarageSwitcher />
                </div>
              )}

              {/* Language Toggle */}
              <div className="mx-1 sm:mx-2">
                <LanguageToggle />
              </div>

              {/* Notifications */}
              {showNotifications && (
                <div className="mx-1 sm:mx-2">
                  <NotificationBell />
                </div>
              )}

              {/* Logout Button - Desktop */}
              <div className="hidden lg:block ml-2">
                <LogoutButton showText={false} className="!px-3" />
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden ml-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                ) : (
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 max-h-[calc(100vh-3.5rem)] overflow-y-auto">
          <div className="px-3 sm:px-4 py-3 space-y-2">
            {/* User Info - Mobile */}
            <div className="flex items-center space-x-3 px-3 py-2 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-[#008B8B] flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                <p className="text-xs text-gray-500 truncate">{getRoleDisplay(displayRole)}</p>
              </div>
            </div>

            {/* Mobile only: Employee and Garage Switchers */}
            {(userData?.role === 'garage_owner' || userData?.role === 'employee') && (
              <>
                <div className="sm:hidden border-t border-gray-100 pt-2">
                  <div className="mb-2">
                    <EmployeeSwitcher />
                  </div>
                  <div className="mb-2">
                    <GarageSwitcher />
                  </div>
                </div>
                <div className="sm:hidden border-b border-gray-100 pb-2"></div>
              </>
            )}

            {/* Menu Items - Mobile */}
            <div className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block text-gray-700 hover:text-[#008B8B] hover:bg-gray-50 rounded-lg transition px-3 py-2 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Logout Button - Mobile */}
            <div className="pt-2 border-t border-gray-100">
              <LogoutButton showText={true} className="w-full justify-center" />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default DashboardHeader;